import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { initializeMsal } from './auth/msalConfig';
import { AuthProvider } from './auth/AuthContext';
import { FirmConfigProvider } from './contexts/FirmConfigContext';
import App from './App';
import DemoPasswordGate from './components/DemoPasswordGate';
import { showGlobalToast } from './utils/toastBridge';
import { applyThemePreference } from './utils/theme';
import { shouldReportToSentry } from './lib/sentryErrorFilter';
import { registerInstallPromptListener } from './hooks/useInstallPrompt';
import { registerServiceWorkerNavigationBridge } from './utils/swNavigationBridge';
import './index.css';

applyThemePreference();

// ── Sentry error tracking ───────────────────────────────────
// DSN injected at build time via env var; no-ops if empty
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // "development" or "production"
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,    // Don't record normal sessions
    replaysOnErrorSampleRate: 1.0,  // Always record sessions with errors
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Report real query failures to Sentry; skip expected/handled client errors
      // (401/403/409 — see shouldReportToSentry) so they don't bury real bugs.
      if (!shouldReportToSentry(error)) return;
      Sentry.captureException(error, {
        tags: { source: 'query' },
        contexts: {
          query: { key: JSON.stringify(query.queryKey) },
        },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Report real mutation failures to Sentry; skip expected/handled client errors
      // (401/403/409) so routine optimistic-concurrency 409s — e.g. two people editing the
      // shared Bulletin Board — don't bury the real failures we need to jump on.
      if (shouldReportToSentry(error)) {
        const mutationKey = mutation.options.mutationKey;
        Sentry.captureException(error, {
          tags: { source: 'mutation' },
          contexts: {
            mutation: {
              key: mutationKey ? JSON.stringify(mutationKey) : undefined,
            },
          },
        });
      }

      // Show user-facing error toast (skip 401s handled by auth interceptor)
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 409) return; // 401 = auth, 409 = handled per-mutation (e.g. checklist gate)
      const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Something went wrong. Please try again.';
      showGlobalToast(msg, 'error');
    },
  }),
});

async function clearLocalDevServiceWorkers() {
  if (!import.meta.env.DEV) return;
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

  const host = window.location.hostname;
  if (host !== 'localhost' && host !== '127.0.0.1') return;

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (error) {
    console.warn('Failed to clear local dev service workers/caches:', error);
  }
}

// P3-A12/PP11: the hand-written src/sw.ts (injectManifest, replacing the old
// generateSW output) deliberately does NOT call self.skipWaiting() at the top
// level — it only does so on receiving a {type: 'SKIP_WAITING'} postMessage
// (see src/sw.ts's own comment for why: an unconditional top-level skipWaiting
// can swap a live tab's cached resources out from under it mid-request).
// Nothing else in this app sends that message (there is no virtual:pwa-register
// import — registration itself is the trivial auto-injected registerSW.js), so
// without this function a new worker would sit in "waiting" forever and
// reloadOnServiceWorkerUpdate() below would never fire — silently breaking the
// entire deploy-reload dance for every user, desktop and mobile alike. This
// reproduces the OLD behavior (the update applies as soon as it's installed)
// through an explicit message instead of an implicit one; it does not change
// when the tab itself becomes visibly reloaded — that's still gated on
// visibility by reloadOnServiceWorkerUpdate() below.
function autoSkipWaitingOnUpdate() {
  if (import.meta.env.DEV) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  navigator.serviceWorker.ready.then((registration) => {
    const trigger = (worker: ServiceWorker | null) => {
      // Only ever fire for a genuine UPDATE (an existing controller means
      // this isn't the very first install) — mirrors the hadController guard
      // reloadOnServiceWorkerUpdate() uses for the same reason.
      if (worker && worker.state === 'installed' && navigator.serviceWorker.controller) {
        worker.postMessage({ type: 'SKIP_WAITING' });
      }
    };
    // A worker may already be sitting in "waiting" by the time we attach
    // (e.g. it finished installing between registration and this call).
    trigger(registration.waiting);
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      newWorker?.addEventListener('statechange', () => trigger(newWorker));
    });
  });
}

// vite-plugin-pwa autoUpdate swaps in the new service worker silently, but the
// already-rendered page keeps running the previous bundle until the user
// navigates — so a stale UI lingers after a deploy. Reload to pick up the new
// bundle, but only once the tab is in the BACKGROUND, so an active deploy never
// wipes a half-finished form (e.g. an intake) out from under the user. We capture
// whether a controller already existed: a first install's controllerchange is the
// SW taking control for the first time (not an update), and must not reload.
function reloadOnServiceWorkerUpdate() {
  if (import.meta.env.DEV) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  const hadController = !!navigator.serviceWorker.controller;
  let refreshing = false;

  const reloadWhenHidden = () => {
    const doReload = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    if (document.visibilityState === 'hidden') {
      doReload();
    } else {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') doReload();
      });
    }
  };

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing || !hadController) return;
    reloadWhenHidden();
  });
}

async function bootstrap() {
  // P3-A8: register the beforeinstallprompt/appinstalled stash FIRST, before
  // MSAL or React render anything — the browser only ever fires
  // beforeinstallprompt once, and only if something has already called
  // preventDefault() + stashed it; a prompt firing during the loading screen
  // must never be missed.
  registerInstallPromptListener();
  // P3-A12/PP12: wire the receiving half of the SW's notificationclick
  // hand-off (src/sw.ts) — re-dispatches a {type:'NAVIGATE', url} postMessage
  // as a window CustomEvent that useSwNavigationBridge (mounted inside
  // <BrowserRouter> in App.tsx) routes via react-router. Registering this
  // early (navigator.serviceWorker itself doesn't need a registration to
  // exist yet for addEventListener to be valid) means no message can arrive
  // before something is listening.
  registerServiceWorkerNavigationBridge();

  await clearLocalDevServiceWorkers();
  const { instance, config } = await initializeMsal();
  // Attach AFTER MSAL has processed any auth-redirect URL — reloading mid
  // code-exchange would consume the OAuth code at the provider but drop it
  // locally, breaking the login loop.
  reloadOnServiceWorkerUpdate();
  autoSkipWaitingOnUpdate();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <DemoPasswordGate>
        <QueryClientProvider client={queryClient}>
          <AuthProvider msalInstance={instance} config={config}>
            <FirmConfigProvider>
              <App />
            </FirmConfigProvider>
          </AuthProvider>
        </QueryClientProvider>
      </DemoPasswordGate>
    </StrictMode>,
  );
}

bootstrap().catch((err) => {
  console.error('Bootstrap failed:', err);
  // Render a minimal error message so the screen isn't blank
  document.getElementById('root')!.innerHTML =
    '<div style="padding:2rem;font-family:sans-serif;color:#ef4444"><h2>Failed to start</h2><pre>' +
    String(err) + '</pre></div>';
});
